import {
	INSPECTION_VALUE_TYPES,
	InspectionTemplateColumn,
} from "@/shared/models/Inspection";
import DeleteIcon from "@mui/icons-material/Delete";
import {
	Autocomplete,
	Grid2,
	IconButton,
	MenuItem,
	TextField,
	Tooltip,
} from "@mui/material";
import { filterGroupNames } from "../utils/columnGroups";

type Props = {
	column: InspectionTemplateColumn;
	groupNames: string[];
	disabled: boolean;
	onChange: (changes: Partial<InspectionTemplateColumn>) => void;
	onGroupPicked: (groupName: string | null) => void;
	onGroupSettled: () => void;
	onRemove: () => void;
};

export const TemplateColumnRow = ({
	column,
	groupNames,
	disabled,
	onChange,
	onGroupPicked,
	onGroupSettled,
	onRemove,
}: Props) => (
	<Grid2
		container
		size={{ xs: 12 }}
		spacing={1}
		alignItems="center"
		data-test-id="column-row"
	>
		<Grid2 size={{ md: 4, xs: 12 }}>
			<Autocomplete
				freeSolo
				options={groupNames}
				filterOptions={filterGroupNames}
				value={column.GroupName ?? ""}
				inputValue={column.GroupName ?? ""}
				disabled={disabled}
				size="small"
				onInputChange={(_event, value) =>
					onChange({ GroupName: value === "" ? null : value })
				}
				onChange={(_event, value) => {
					const groupName = typeof value === "string" ? value.trim() : "";
					onGroupPicked(groupName === "" ? null : groupName);
				}}
				onBlur={onGroupSettled}
				renderInput={(params) => (
					<TextField
						{...params}
						label="Group"
						size="small"
						autoComplete="off"
						slotProps={{
							htmlInput: {
								...params.inputProps,
								maxLength: 100,
								"data-test-id": "column-group-input",
							},
						}}
					/>
				)}
			/>
		</Grid2>
		<Grid2 size={{ md: 4, xs: 12 }}>
			<TextField
				label="Label"
				required
				slotProps={{
					htmlInput: {
						maxLength: 100,
						"data-test-id": "column-label-input",
					},
				}}
				error={column.ColumnLabel !== "" && column.ColumnLabel.trim() === ""}
				value={column.ColumnLabel}
				disabled={disabled}
				fullWidth
				size="small"
				autoComplete="off"
				onChange={(event) => onChange({ ColumnLabel: event.target.value })}
			/>
		</Grid2>
		<Grid2 size={{ md: 3, xs: 12 }}>
			<Tooltip
				title={
					column.HasReadings
						? "This column was already read on inspections, so its type cannot change."
						: ""
				}
			>
				<TextField
					select
					label="Type"
					data-test-id="column-type-select"
					value={column.ValueTypeCode}
					disabled={disabled || column.HasReadings}
					fullWidth
					size="small"
					onChange={(event) =>
						onChange({
							ValueTypeCode: event.target
								.value as InspectionTemplateColumn["ValueTypeCode"],
						})
					}
				>
					{INSPECTION_VALUE_TYPES.map((option) => (
						<MenuItem key={option.code} value={option.code}>
							{option.label}
						</MenuItem>
					))}
				</TextField>
			</Tooltip>
		</Grid2>
		<Grid2 size="auto">
			<Tooltip
				title={
					column.HasReadings
						? "This column was already read on inspections, so it cannot be removed."
						: "Remove"
				}
			>
				<span>
					<IconButton
						size="small"
						data-test-id="remove-column-button"
						disabled={disabled || column.HasReadings}
						onClick={onRemove}
					>
						<DeleteIcon
							fontSize="small"
							color={disabled || column.HasReadings ? "disabled" : "secondary"}
						/>
					</IconButton>
				</span>
			</Tooltip>
		</Grid2>
	</Grid2>
);
